import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCircle,
  faExternalLinkAlt,
  faTag,
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { AWSResource, GCPResource, GitHubResource } from '../types';

type Resource = AWSResource | GCPResource | GitHubResource;

interface ResourceTableProps {
  resources: Resource[];
  type: 'aws' | 'gcp' | 'github';
  loading?: boolean;
  maxItems?: number;
}

export const ResourceTable: React.FC<ResourceTableProps> = ({
  resources,
  type,
  loading = false,
  maxItems = 10
}) => {
  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="grid grid-cols-4 gap-3 mb-2 p-2 bg-gray-50 rounded">
          <div className="h-3 bg-gray-200 rounded"></div>
          <div className="h-3 bg-gray-200 rounded"></div>
          <div className="h-3 bg-gray-200 rounded"></div>
          <div className="h-3 bg-gray-200 rounded"></div>
        </div>
        {[...Array(5)].map((_, i) => (
          <div key={i} className="grid grid-cols-4 gap-3 mb-2 p-2">
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
            <div className="h-2 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!resources.length) {
    return (
      <div className="text-center py-6 text-gray-500">
        <div className="text-xs">No resources found</div>
      </div>
    );
  }

  const displayResources = resources.slice(0, maxItems);
  const hasMore = resources.length > maxItems;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running':
      case 'available':
      case 'active':
        return 'text-green-500';
      case 'stopped':
      case 'inactive':
        return 'text-red-500';
      case 'pending':
      case 'terminating':
        return 'text-yellow-500';
      default:
        return 'text-gray-500';
    }
  };

  const formatCost = (cost?: number) => {
    if (!cost) return '-';
    return `$${cost.toFixed(2)}`;
  };

  const renderAWSResource = (resource: AWSResource) => (
    <div key={resource.id} className="grid grid-cols-4 gap-3 items-center py-1.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon
          icon={faCircle}
          className={`text-[8px] ${getStatusColor(resource.status)}`}
        />
        <div>
          <div className="font-bold text-xs text-gray-900">{resource.name}</div>
          <div className="text-xs text-gray-500">{resource.type.toUpperCase()}</div>
        </div>
      </div>
      <div className="text-xs text-gray-600">{resource.region}</div>
      <div className="text-xs text-gray-600">{resource.status}</div>
      <div className="text-xs text-gray-900 font-bold">{formatCost(resource.cost)}</div>
    </div>
  );

  const renderGCPResource = (resource: GCPResource) => (
    <div key={resource.id} className="grid grid-cols-4 gap-3 items-center py-1.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon
          icon={faCircle}
          className={`text-[8px] ${getStatusColor(resource.status)}`}
        />
        <div>
          <div className="font-bold text-xs text-gray-900">{resource.name}</div>
          <div className="text-xs text-gray-500">{resource.type.toUpperCase()}</div>
        </div>
      </div>
      <div className="text-xs text-gray-600">{resource.zone || resource.region || '-'}</div>
      <div className="text-xs text-gray-600">{resource.status}</div>
      <div className="text-xs text-gray-900 font-bold">{formatCost(resource.cost)}</div>
    </div>
  );

  const renderGitHubResource = (resource: GitHubResource) => (
    <div key={resource.id} className="grid grid-cols-4 gap-3 items-center py-1.5 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon
          icon={faCircle}
          className={`text-[8px] ${getStatusColor(resource.status)}`}
        />
        <div>
          <div className="font-bold text-xs text-gray-900">{resource.name}</div>
          <div className="text-xs text-gray-500">{resource.type}</div>
        </div>
      </div>
      <div className="text-xs text-gray-600">{resource.language || resource.visibility || '-'}</div>
      <div className="text-xs text-gray-600">{resource.status}</div>
      <div className="text-xs text-gray-900">
        {resource.stars ? `${resource.stars} stars` : '-'}
      </div>
    </div>
  );

  const renderResource = (resource: Resource) => {
    switch (type) {
      case 'aws':
        return renderAWSResource(resource as AWSResource);
      case 'gcp':
        return renderGCPResource(resource as GCPResource);
      case 'github':
        return renderGitHubResource(resource as GitHubResource);
      default:
        return null;
    }
  };

  const getHeaders = () => {
    switch (type) {
      case 'aws':
        return ['Name', 'Region', 'Status', 'Cost'];
      case 'gcp':
        return ['Name', 'Zone/Region', 'Status', 'Cost'];
      case 'github':
        return ['Name', 'Details', 'Status', 'Stats'];
      default:
        return ['Name', 'Location', 'Status', 'Info'];
    }
  };

  return (
    <div className="overflow-hidden">
      <div className="grid grid-cols-4 gap-3 mb-2 p-2 bg-gray-100 rounded border border-gray-300">
        {getHeaders().map((header, index) => (
          <div key={index} className="text-xs font-bold text-gray-700 uppercase tracking-wider">
            {header}
          </div>
        ))}
      </div>
      <div className="space-y-0">
        {displayResources.map(renderResource)}
      </div>
      {hasMore && (
        <div className="mt-2 p-2 text-center">
          <span className="text-xs text-gray-500">
            Showing {maxItems} of {resources.length} resources
          </span>
        </div>
      )}
    </div>
  );
};